// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { headerText } from "@conklin/App/SharedStyles/AutoCompleteSharedStyles";
import { createHorizontalSmallHeadingAndTextStyles } from "@conklin/App/SharedStyles/SmallHeadingAndTextStyles";
import { ProductPriceStyles } from "@conklin/Overrides/Components/ProductPrice";
import { autocompleteProductsStyles } from "@conklin/Overrides/Widgets/Header/AutocompleteProducts";
import IconArrowRight from "@conklin/XDL/Icons/IconArrowRight";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, mergeWithCss, xScreen } from "@conklin/XDL/Utilities";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const priceStyle: TypographyPresentationProps = {
    size: 16,
    lineHeight: "19px",
    css: css`
        &&&& {
            line-height: 19px;
        }
    `,
};

autocompleteProductsStyles.header = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    `,
};

autocompleteProductsStyles.headerText = {
    ...mergeWithCss(headerText, {
        css: css`
            margin: 0;
        `,
    }),
};

autocompleteProductsStyles.headerLink = {
    ...createLinkIconStyles(IconArrowRight, "small", "right"),
};
autocompleteProductsStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: 72px 1fr 206px;
        gap: 8px 12px;
        border-bottom: 1px solid ${color("common.border")};
        padding-bottom: 12px;

        &:not(:last-of-type) {
            margin-bottom: 12px;
        }

        &:last-of-type {
            border-bottom: none;
            padding-bottom: 0;
        }
    `,
};

autocompleteProductsStyles.focusedContainer = {
    ...mergeWithCss(autocompleteProductsStyles.container, {
        css: css`
            background-color: ${color("common.ui3")};
        `,
    }),
};

autocompleteProductsStyles.imageClickable = {
    css: css`
        max-width: 72px;
        max-height: 72px;
        flex-basis: 72px;
    `,
};

autocompleteProductsStyles.image = {
    css: css`
        width: 72px;
        min-width: 72px;
        height: 72px;
        cursor: pointer;
        object-fit: contain;
    `,
};
autocompleteProductsStyles.infoGridItem = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        ${xScreen("lte", "md")} {
            padding: 0;
            grid-column: 2/4;
        }
    `,
};

autocompleteProductsStyles.actionsGridItem = {
    css: css`
        display: flex;
        flex-direction: column;
        grid-column: 3/3;
        row-gap: 8px;
        ${xScreen("lte", "md")} {
            grid-column: 2/4;
            grid-row: 2/2;
        }
    `,
};

autocompleteProductsStyles.titleLink = {
    color: "text.main",
    typographyProps: {
        size: 12,
        weight: fontConfig.weight.bold,
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 15px;
            vertical-align: unset;
            ${wrapInContainerStyles}
            ${ellipsis(2)};
            ${xScreen("lte", "md")} {
                ${ellipsis(3)}
            }
        `,
    },
    css: css`
        width: auto;
        margin-bottom: 8px;
        align-items: flex-start;
        -webkit-box-align: start;

        &:hover {
            ${TypographyStyle} {
                color: ${color("common.ui10")};
            }
        }
    `,
};

autocompleteProductsStyles.productNumbersContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        ${xScreen("lte", "sm")} {
            margin-bottom: 12px;
        }
    `,
};

autocompleteProductsStyles.erpNumberText = {
    ...createHorizontalSmallHeadingAndTextStyles(),
};

autocompleteProductsStyles.actions = {
    addToCartContainer: {
        css: css`
            min-width: 0;
            flex-wrap: nowrap;
            gap: 12px;
        `,
    },
    productAddToCartButtonStyles: {
        sizeVariant: "small",
        typographyProps: {
            size: 11,
            lineHeight: "13px;",
        },
    },
    addToCartButtonWrapper: {
        css: css`
            min-width: unset;
            max-width: 200px;
        `,
    },
    qtyWrapper: {
        css: css`
            width: 60px;
        `,
    },
    qtyField: {
        qtyFieldStyles: {
            wrapper: {
                css: css`
                    width: 60px;
                    min-width: 60px;
                `,
            },
            editableQuantityTextField: {
                cssOverrides: {
                    inputSelect: css`
                        height: 32px;
                    `,
                },
            },
            actionButton: {
                css: css`
                    display: none;
                `,
            },
        },
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

autocompleteProductsStyles.productPriceStyles = {
    price: {
        unitOfMeasure: {
            unitOfMeasureText: {
                size: 12,
                lineHeight: "15px",
            },
        },
        realTimeText: {
            size: 14,
            lineHeight: "22px",
        },
        priceText: priceStyle,
        errorText: priceStyle,
        invalidPriceText: priceStyle,
    },
    invalidPriceText: priceStyle,
    quoteMessage: {
        text: priceStyle,
    },
    quoteMessageWrapper: {
        css: css`
            white-space: nowrap;
        `,
    },
} as ProductPriceStyles;

autocompleteProductsStyles.brandStyles = {
    nameText: {
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 2px;
        `,
    },
    nameLink: {
        typographyProps: {
            weight: fontConfig.weight.regular,
        },
        css: css`
            &:hover ${TypographyStyle} {
                color: ${color("common.ui10")};
            }
            margin-bottom: 2px;
        `,
    },
};
autocompleteProductsStyles.productPriceWrapper = {
    css: css`
        display: flex;
        column-gap: 4px;
    `,
};
autocompleteProductsStyles.variantProductPriceLabel = {
    variant: "h6",
    forwardAs: "p",
    weight: fontConfig.weight.regular,
    css: css`
        margin-bottom: 0;
    `,
};
